import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Play, LockKey, DownloadSimple, Crown, SpeakerHigh, SpeakerSlash, ArrowUpRight, Megaphone } from "@phosphor-icons/react";

const fmt = (s) => {
  const t = Math.max(0, Math.ceil(s));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};

/**
 * The sponsor gate shown to free members before a scenepack download: the ad
 * (video or image) with its text and link, then the pack with a Download button
 * that stays locked until the ad has finished.
 *
 * Also used as the live preview in the admin panel (`preview`), where the
 * Download button only shows a message.
 *
 * `ad`: { title, description, button_label, link_url, logo_url, video_url, image_url }
 * `minSeconds`: the download never unlocks before this many seconds have passed.
 * `onDownload`: called when the unlocked button is pressed.
 * `stickyFooter`: on phones, keep the Download / Get AA+ buttons pinned to the bottom of the
 * window so they never have to be scrolled to (the real gate sets it; the admin preview doesn't).
 */
export default function SponsorGateCard({ ad, pack, minSeconds = 15, onDownload, onCloseGate, preview = false, stickyFooter = false }) {
  const videoRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [videoDone, setVideoDone] = useState(!ad.video_url);
  const [videoLeft, setVideoLeft] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [busy, setBusy] = useState(false);

  // Seconds since the gate opened (drives the minimum wait, and the image-only countdown).
  useEffect(() => {
    const started = Date.now();
    const id = setInterval(() => setElapsed((Date.now() - started) / 1000), 250);
    return () => clearInterval(id);
  }, []);

  const unlocked = videoDone && elapsed >= minSeconds;
  const waitLeft = Math.max(minSeconds - elapsed, videoDone ? 0 : videoLeft ?? 0);
  const progress = ad.video_url
    ? videoDone ? 1 : videoLeft !== null && videoRef.current?.duration ? 1 - videoLeft / videoRef.current.duration : 0
    : Math.min(1, elapsed / minSeconds);

  const click = async () => {
    if (!unlocked || busy) return;
    if (preview) { toast("Preview only — this is where the download would start."); return; }
    setBusy(true);
    try { await onDownload?.(); } finally { setBusy(false); }
  };

  const playVideo = () => videoRef.current?.play().catch(() => {});
  // Some phones refuse to start a video by themselves: say what to do instead of a stuck "0:00".
  const needsPlay = !unlocked && !videoDone && !playing && elapsed >= 2 && (videoLeft === null || videoLeft > 0);
  const label = unlocked ? (busy ? "Starting..." : "Download") : needsPlay ? "Play the video to unlock" : `Unlocks in ${fmt(waitLeft)}`;

  const buttons = (testId) => (
    <>
      <button
        type="button"
        onClick={needsPlay ? playVideo : click}
        disabled={(!unlocked && !needsPlay) || busy}
        data-testid={testId}
        className={`flex items-center justify-center gap-2 py-3 rounded-md text-sm font-display font-bold transition ${
          unlocked
            ? "bg-[#880015] hover:bg-[#A6001A] text-white shadow-[0_0_24px_rgba(136,0,21,0.4)]"
            : needsPlay
              ? "bg-white/10 text-white border border-white/20"
              : "bg-white/5 text-white/35 border border-white/10 cursor-not-allowed"
        }`}
      >
        {unlocked ? <DownloadSimple size={16} /> : needsPlay ? <Play size={16} weight="fill" /> : <LockKey size={16} />}
        {label}
      </button>
      <Link
        to="/aa-plus"
        onClick={onCloseGate}
        className="flex items-center justify-center gap-2 py-3 rounded-md text-sm font-display font-bold border border-white/15 bg-white/5 hover:bg-white/10 transition"
      >
        <Crown size={15} weight="fill" className="text-[#FF8090]" /> Get AA+
      </Link>
    </>
  );
  const character = pack.character_name && pack.character_name !== pack.title ? pack.character_name : "";

  return (
    <div className="p-5 md:p-6" data-testid="sponsor-gate">
      {/* Sponsored header */}
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.35em] text-white/40 mb-3">
        <Megaphone size={13} className="text-[#FF8090]" /> Sponsored
      </div>
      <div className="flex items-center gap-3 mb-4">
        {ad.logo_url && <img src={ad.logo_url} alt="" className="w-11 h-11 rounded-md object-cover bg-white/10 flex-shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-[15px] md:text-base leading-snug uppercase break-words" data-testid="sponsor-title">{ad.title || "Your sponsor headline"}</div>
          {ad.description && <div className="text-xs text-white/50 mt-0.5 line-clamp-2">{ad.description}</div>}
        </div>
        {ad.link_url && (
          <a
            href={ad.link_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid="sponsor-visit"
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs font-display font-bold px-4 py-2 rounded-md border border-white/15 bg-white/5 hover:bg-white/10 transition"
          >
            {ad.button_label || "Visit"} <ArrowUpRight size={12} />
          </a>
        )}
      </div>

      {/* Ad media */}
      <div className="relative rounded-lg overflow-hidden border border-white/10 bg-[#1b1b1b] aspect-[16/7] md:aspect-[16/6.5]">
        {ad.image_url && (
          <img src={ad.image_url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-2xl opacity-40 scale-110" />
        )}
        {ad.video_url ? (
          <>
            <video
              ref={videoRef}
              src={ad.video_url}
              poster={ad.image_url || undefined}
              autoPlay
              muted={muted}
              playsInline
              preload="auto"
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => { setVideoDone(true); setPlaying(false); }}
              onError={() => setVideoDone(true)} // a broken video must never trap the download
              onTimeUpdate={(e) => setVideoLeft(e.currentTarget.duration - e.currentTarget.currentTime)}
              className="relative w-full h-full object-contain"
              data-testid="sponsor-video"
            />
            {!playing && !videoDone && (
              <button type="button" onClick={playVideo} aria-label="Play" className="absolute inset-0 flex items-center justify-center bg-black/30">
                <span className="w-16 h-16 rounded-full bg-black/55 backdrop-blur flex items-center justify-center border border-white/20"><Play size={26} weight="fill" /></span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              aria-label={muted ? "Turn sound on" : "Turn sound off"}
              className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/55 backdrop-blur flex items-center justify-center hover:bg-black/75 transition"
            >
              {muted ? <SpeakerSlash size={16} /> : <SpeakerHigh size={16} />}
            </button>
          </>
        ) : ad.image_url ? (
          <img src={ad.image_url} alt="" className="relative w-full h-full object-contain" />
        ) : (
          <div className="relative w-full h-full flex items-center justify-center text-white/25 text-xs font-mono">Your ad video or image appears here</div>
        )}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
          <div className="h-full bg-[#880015] transition-[width] duration-200" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
      </div>

      <p className="text-xs text-white/45 mt-3">
        Want to skip sponsor gates?{" "}
        <Link to="/aa-plus" onClick={onCloseGate} className="font-bold text-white hover:text-[#FF8090] transition">AA+ starts downloads instantly.</Link>
      </p>

      {/* The pack + the locked / unlocked download button */}
      <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4 flex flex-col sm:flex-row gap-4">
        <div className="w-full sm:w-32 aspect-[4/3] sm:aspect-[3/4] rounded-md overflow-hidden bg-[#232323] flex-shrink-0">
          {pack.thumbnail_url && <img src={pack.thumbnail_url} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="font-display font-bold text-lg leading-snug mb-3">{pack.title || "Scenepack"}</div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs mb-4">
            {[
              ["Creator", pack.creator_name],
              ["From", `${pack.movie_title || ""}${pack.year ? ` (${pack.year})` : ""}`],
              ["Character", character || pack.character_name],
              ["File size", pack.file_size],
              ["Quality", pack.quality],
            ].filter(([, v]) => v).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] font-mono uppercase tracking-wider text-white/35">{k}</dt>
                <dd className="text-white/80 mt-0.5 truncate">{v}</dd>
              </div>
            ))}
          </dl>
          {/* on phones the buttons live in the pinned footer below instead */}
          <div className={`mt-auto grid-cols-2 gap-3 ${stickyFooter ? "hidden md:grid" : "grid"}`}>
            {buttons("sponsor-download")}
          </div>
        </div>
      </div>

      {stickyFooter && (
        <div
          className="md:hidden sticky bottom-0 z-10 -mx-5 -mb-5 mt-4 px-5 pt-3 grid grid-cols-2 gap-3 bg-[#141414] border-t border-white/10"
          style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
          data-testid="sponsor-gate-footer"
        >
          {buttons("sponsor-download-mobile")}
        </div>
      )}
    </div>
  );
}
